import type { ComponentType } from "react";
import { Chip } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { TranslationRow } from "./types";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { Stack, Typography } from "@mui/material";

export interface TranslationCellComponents {
  VariantsCell: ComponentType<{ translation: TranslationRow }>;
  SyncTranslationButton: ComponentType<{ translation: TranslationRow }>;
}

export function getColumnsConfig({
  VariantsCell,
  SyncTranslationButton,
}: TranslationCellComponents): ColumnDefinition<TranslationRow>[] {
  return [
    { name: "id", label: "ID", options: { display: false, filter: false } },
    {
      name: "path",
      label: "Путь",
      options: {
        setCellHeaderProps: () => ({ style: { minWidth: 260 } }),
        customBodyRender: (data: unknown) => {
          const row = data as TranslationRow;
          return (
            <CrudLink id={row.id} tab="details">
              {`${row.project}/${row.namespace}/${row.version}`}
            </CrudLink>
          );
        },
      },
    },
    {
      name: "version",
      label: "Версия",
      options: {
        setCellHeaderProps: () => ({ style: { width: 140 } }),
        customBodyRender: (value: unknown) => (
          <Chip
            label={value as string}
            color={value === "latest" ? "primary" : "default"}
            size="small"
          />
        ),
      },
    },
    {
      name: "variants",
      label: "Языки",
      options: {
        setCellHeaderProps: () => ({ style: { minWidth: 280 } }),
        customBodyRender: (data: unknown) => (
          <VariantsCell translation={data as TranslationRow} />
        ),
      },
    },
    {
      name: "sync",
      label: "",
      options: {
        setCellHeaderProps: () => ({ style: { width: 60 } }),
        customBodyRender: (data: unknown) => (
          <SyncTranslationButton translation={data as TranslationRow} />
        ),
      },
    },
    {
      name: "updatedAt",
      label: "Обновлён",
      options: {
        setCellHeaderProps: () => ({ style: { width: 150 } }),
        customBodyRender: (value: unknown) => (
          <CustomCellDate date={value as string} />
        ),
      },
    },
  ];
}

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Переводы",
  itemCreated: "Перевод создан",
  itemUpdated: "Перевод обновлён",
  createItem: "Создать перевод",
  refetchList: "Обновить список",
  deleteItems: "Удалить",
  itemsDeleted: "Удалено",
  restoreItems: "Восстановить",
  itemsRestored: "Восстановлено",
  cancel: "Отмена",
  editItem: "Перевод",
  showTitle: "Детали",
});

// Колонки для мобильного представления: путь перевода и языки
export function getMobileColumnsConfig({
  VariantsCell,
}: Pick<
  TranslationCellComponents,
  "VariantsCell"
>): DataListViewColumns<TranslationRow> {
  return {
    main: {
      title: "Перевод",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <CrudLink id={row.id} tab="details" noWrap>
            {`${row.project}/${row.namespace}`}
          </CrudLink>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.version}
          </Typography>
        </Stack>
      ),
    },
    variants: {
      title: "Языки",
      sx: { width: 140, flexShrink: 0 },
      renderCell: (row) => <VariantsCell translation={row} />,
    },
  };
}
